/**
 * Um turno, com o extrato e a contagem por denominação.
 *
 * É a tela da CONFERÊNCIA, e por isso ela mostra a contagem nota a nota. É o
 * que transforma "faltam R$ 40" em "faltam duas notas de 20" — que é uma
 * pista, e não um mistério.
 *
 * O código na URL é o `T0001`, nunca o uuid (CONSTITUICAO.md §2).
 */
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Icone } from '../../componentes/Icone'
import { Selo } from '../../componentes/Selo'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { carregarTurno, type FichaTurno } from '../../plataforma/caixa'
import { LayoutApp } from '../app/LayoutApp'
import { CartaoIndicador } from './componentes/CartaoIndicador'
import { PainelGaveta } from './componentes/PainelGaveta'
import { TabelaMovimentos } from './componentes/TabelaMovimentos'

export function TelaTurno() {
  const { codigo = '' } = useParams()
  const [ficha, setFicha] = useState<FichaTurno | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [falha, setFalha] = useState('')

  useEffect(() => {
    const controle = new AbortController()

    carregarTurno(codigo, controle.signal)
      .then((resposta) => {
        if (!controle.signal.aborted) setFicha(resposta)
      })
      .catch(() => {
        if (!controle.signal.aborted) setFalha('Turno não encontrado.')
      })
      .finally(() => {
        if (!controle.signal.aborted) setCarregando(false)
      })

    return () => controle.abort()
  }, [codigo])

  const turno = ficha?.turno

  return (
    <LayoutApp
      titulo={`Turno ${codigo}`}
      subtitulo={
        turno
          ? `${turno.terminal} · ${dataHoraDe(turno.aberto_em)}${
              turno.fechado_em ? ` até ${dataHoraDe(turno.fechado_em)}` : ''
            }`
          : 'Carregando…'
      }
      acoes={
        <Link
          to="/app/caixa/turnos"
          className="flex h-[34px] items-center gap-2 rounded-sistema border border-borda bg-white px-4 text-[13.5px] font-semibold whitespace-nowrap text-marca"
        >
          <Icone nome="view_list" tamanho={18} />
          Voltar ao histórico
        </Link>
      }
    >
      <div className="flex w-full flex-col gap-4">
        {carregando && <p className="px-1 py-8 text-sm text-texto-suave">Carregando o turno…</p>}

        {falha && (
          <div className="rounded-sistema border border-erro-barra bg-erro-fundo px-4 py-3 text-[13px] text-erro-forte">
            {falha}
          </div>
        )}

        {ficha && turno && (
          <>
            <ResumoDoFechamento ficha={ficha} />

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <CartaoIndicador
                rotulo="Abertura"
                icone="payments"
                valor={turno.abertura_centavos}
                detalhe={turno.aberto_por ? `por ${turno.aberto_por}` : undefined}
              />
              <CartaoIndicador
                rotulo="Vendas em dinheiro"
                icone="point_of_sale"
                valor={turno.vendas_dinheiro_centavos}
              />
              <CartaoIndicador
                rotulo="Cartão e PIX"
                icone="trending_up"
                valor={turno.cartao_pix_centavos}
                detalhe="Fora da gaveta"
              />
              <CartaoIndicador
                rotulo="Vendas do turno"
                icone="receipt_long"
                valor={turno.vendas_total_centavos}
                detalhe={`${turno.quantidade_vendas} ${
                  turno.quantidade_vendas === 1 ? 'venda' : 'vendas'
                }`}
              />
            </div>

            <div className="grid items-start gap-4 xl:grid-cols-[1.55fr_1fr]">
              <section className="min-w-0 rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
                <div className="mb-4">
                  <h2 className="font-titulo text-base font-semibold text-tinta">
                    Movimentos do turno
                  </h2>
                </div>
                <TabelaMovimentos movimentos={ficha.movimentos} />
              </section>

              <div className="flex min-w-0 flex-col gap-4">
                <PainelGaveta
                  turno={turno}
                  podeMovimentar={false}
                  aoSangrar={() => {}}
                  aoSuprir={() => {}}
                />
                <Contagem ficha={ficha} />
              </div>
            </div>
          </>
        )}
      </div>
    </LayoutApp>
  )
}

function ResumoDoFechamento({ ficha }: { ficha: FichaTurno }) {
  const { turno } = ficha

  if (turno.status === 'aberto') {
    return (
      <div className="flex gap-3 rounded-sistema border border-marca-barra bg-marca-fundo-2 px-4 py-3.5">
        <Icone nome="info" tamanho={20} className="text-marca" />
        <p className="text-[13px] text-marca-forte">
          Este turno ainda está aberto. Os números mudam a cada venda — o esperado só vira número
          definitivo no fechamento.
        </p>
      </div>
    )
  }

  const diferenca = turno.diferenca_centavos ?? 0

  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-titulo text-base font-semibold text-tinta">Fechamento</h2>
          <p className="mt-[3px] text-[12.5px] text-texto-suave">
            Fechado {turno.fechado_em ? `em ${dataHoraDe(turno.fechado_em)}` : ''}
            {turno.fechado_por ? ` por ${turno.fechado_por}` : ''}
          </p>
        </div>
        {diferenca === 0 ? (
          <Selo tom="ok">Bateu</Selo>
        ) : (
          <Selo tom={diferenca < 0 ? 'erro' : 'atencao'}>
            {diferenca < 0 ? 'Quebra' : 'Sobra'}
          </Selo>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Numero rotulo="Contado" valor={turno.contado_centavos ?? 0} />
        <Numero rotulo="Esperado" valor={turno.esperado_centavos ?? 0} />
        <Numero
          rotulo="Diferença"
          valor={Math.abs(diferenca)}
          tom={diferenca === 0 ? 'neutro' : diferenca < 0 ? 'erro' : 'atencao'}
        />
      </div>

      {turno.observacao && (
        <div className="mt-4 border-t border-divisoria pt-3">
          <p className="text-[12px] font-semibold text-texto-suave">Observação</p>
          <p className="mt-1 text-[13px] leading-[1.55] text-texto">{turno.observacao}</p>
        </div>
      )}
    </section>
  )
}

function Numero({
  rotulo,
  valor,
  tom = 'neutro',
}: {
  rotulo: string
  valor: number
  tom?: 'neutro' | 'erro' | 'atencao'
}) {
  const cor = tom === 'erro' ? 'text-erro' : tom === 'atencao' ? 'text-atencao' : 'text-tinta'
  return (
    <div>
      <p className="text-[12.5px] text-texto-suave">{rotulo}</p>
      <p className={`tabular mt-1 font-titulo text-[22px] font-bold ${cor}`}>
        {formatarMoeda(valor)}
      </p>
    </div>
  )
}

function Contagem({ ficha }: { ficha: FichaTurno }) {
  if (ficha.contagem.length === 0) return null

  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <div className="mb-3">
        <h2 className="font-titulo text-base font-semibold text-tinta">Contagem da gaveta</h2>
        <p className="mt-[3px] text-[12.5px] text-texto-suave">
          Nota a nota, como foi contada no fechamento
        </p>
      </div>

      <table className="w-full border-collapse">
        <caption className="sr-only">Contagem por denominação</caption>
        <tbody>
          {ficha.contagem.map((linha) => (
            <tr key={linha.denominacao_centavos} className="border-b border-divisoria last:border-0">
              <td className="tabular py-1.5 text-[13px] text-texto-suave">
                {formatarMoeda(linha.denominacao_centavos)}
              </td>
              <td className="tabular py-1.5 text-center text-[13px] text-texto">
                × {linha.quantidade}
              </td>
              <td className="tabular py-1.5 text-right text-[13px] font-semibold text-tinta">
                {formatarMoeda(linha.total_centavos)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

function dataHoraDe(iso: string): string {
  const data = new Date(iso)
  return Number.isNaN(data.getTime())
    ? '—'
    : data.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
}
